<script setup>
import { nextTick, ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import T from './CompanyProfileAboutTab.i18n.js';
import DsLinkBlock from 'JIX/components/ds/DsLinkBlock.vue';
import DefaultMap from 'JIX/map/DefaultMap.js';
import ClusteredJobs from 'JIX/map/ClusteredJobs.js';
import SvgIcon from 'JIX/components/SvgIcon.vue';
import { getLocale } from 'JIX/utils.js';
import { getDateFormatter } from 'JIX/utils/date.js';
import CompanyProfileAvailableJobsSection from './CompanyProfileAvailableJobsSection.vue';
import { adjustCarouselHeight } from 'JIX/ui/testimonialCarousel.js';

import { getStandardLineChartConfig } from 'JIX/highcharts/configs/line_chart.js';

import 'icons/share-facebook.svg';
import 'icons/share-instagram.svg';
import 'icons/share-youtube.svg';
import 'icons/share-linkedin.svg';
import 'icons/share-twitter.svg';

const store = useStore();

const STAT_LAST_YEAR = 'lastyear';
const STAT_LAST_MONTH = 'lastmonth';

function generateMonthNames(format) {
    const locale = getLocale();
    return Array.from({ length: 12 }, (_, i) => {
        const date = new Date();
        date.setMonth(i);
        return date.toLocaleDateString(locale, { month: format });
    });
}

const months = generateMonthNames('short');
const fullMonths = generateMonthNames('long');

const emit = defineEmits(['jobTab']);

const scriptDoms = ref([]);
const map = ref(null);
const clusteredJobs = ref(null);
const timeInterval = ref(STAT_LAST_YEAR);
const actionOrder = ref(['writeNews', 'followerStatistics', 'ratings', 'editCompany']);
const statOptions = ref([
    [T.latestYear(), STAT_LAST_YEAR],
    [T.latestMonth(), STAT_LAST_MONTH],
]);

const mapRef = ref();

onMounted(() => {
    if ('about' in innerScripts.value) {
        for (let script of innerScripts.value.about) {
            const scriptElem = document.createElement('script');
            scriptDoms.value.push(scriptElem);
            scriptElem.innerHTML = script;
            document.head.appendChild(scriptElem);
        }
    }

    if (aboutContent.value.address && aboutContent.value.geojson) {
        nextTick().then(() => {
            map.value = new DefaultMap({ container: mapRef.value });
            clusteredJobs.value = new ClusteredJobs({}).addTo(map.value).setData(aboutContent.value.geojson, true);
        });
    }

    adjustCarouselHeight();
    window.addEventListener('resize', adjustCarouselHeight);
});

onUnmounted(() => {
    window.removeEventListener('resize', adjustCarouselHeight);

    for (let dom of scriptDoms.value) {
        dom.remove();
    }
});

const innerScripts = computed(() => store.state.innerScripts);
const aboutContent = computed(() => store.state.aboutContent);
const isFree = computed(() => store.state.isFree);
const isAdm = computed(() => store.state.isAdm);
const isAdmInternal = computed(() => store.state.isAdmInternal);
const companyName = computed(() => store.state.companyName);

const clickStatistics = computed(() => {
    if (timeInterval.value === STAT_LAST_MONTH) {
        return aboutContent.value.monthStats;
    } else if (timeInterval.value === STAT_LAST_YEAR) {
        return aboutContent.value.yearStats;
    }
    return null;
});

const statDataSeries = computed(() => {
    if (!clickStatistics.value) {
        return null;
    }
    const exposures = [];
    const clicks = [];
    for (let stat of clickStatistics.value) {
        clicks.push(stat.clicks);
        exposures.push(stat.exposures);
    }
    return [
        {
            name: T.series1Legend(),
            data: exposures,
        },
        {
            name: T.series2Legend(),
            data: clicks,
        }
    ];
});

const statXAxisCategories = computed(() => {
    if (!clickStatistics.value) {
        return null;
    }

    if (timeInterval.value === STAT_LAST_MONTH) {
        const dateFormatter = getDateFormatter();
        return clickStatistics.value.map((element) => {
            const date = new Date(element.date);
            return dateFormatter(date, { month: "short", addYear: false });
        });
    } else if (timeInterval.value === STAT_LAST_YEAR) {
        return clickStatistics.value.map((element) => { return months[new Date(element.date).getMonth()]; });
    }
    return null;
});

const highchartOptions = computed(() => {
    const timeIntervalFormatter = timeInterval.value;
    const c1 = '#2f786c';
    const c2 = '#d2bd21';
    return getStandardLineChartConfig({
        chart: {
            borderWidth: 0,
        },
        title: {
            text: "",
        },
        xAxis: {
            tickInterval: 2,
            tickmarkPlacement: "on",
            tickWidth: 1,
            gridLineWidth: 1,
            labels: {
                formatter: null,
                rotation: 315,
            },
            categories: statXAxisCategories.value,
        },
        yAxis: {
            allowDecimals: false,
        },
        tooltip: {
            formatter: function() {
                const y_local = this.series.options.custom.getLocaleNumbers(this.y);
                if (timeIntervalFormatter === STAT_LAST_MONTH) {
                    return T.graphFormatterText(this.x, y_local);
                } else if (timeIntervalFormatter === STAT_LAST_YEAR) {
                    return T.graphFormatterText(fullMonths[months.indexOf(this.x)], y_local);
                }
                return '';
            }
        },
        colors: [c1, c2],
        series: statDataSeries.value,
    });
});

const noExposures = computed(() => {
    let exposures;
    if (timeInterval.value === STAT_LAST_YEAR) {
        exposures = aboutContent.value.yearExposures;
    } else if (timeInterval.value === STAT_LAST_MONTH) {
        exposures = aboutContent.value.monthExposures;
    }
    return exposures;
});

const noClicks = computed(() => {
    let clicks;
    if (timeInterval.value === STAT_LAST_YEAR) {
        clicks = aboutContent.value.yearClicks;
    } else if (timeInterval.value === STAT_LAST_MONTH) {
        clicks = aboutContent.value.monthClicks;
    }
    return clicks;
});

const clickHeader = computed(() => {
    const intervalText = statOptions.value.find(x => x[1] === timeInterval.value)[0];
    return T.clickHeader(intervalText.toLowerCase());
});

const showCompanyRelations = computed(() => !isFree.value && Object.keys(aboutContent.value.daughterCompanies).length !== 0);
const showCompanyInfoBox = computed(() => {
    return Boolean(
        aboutContent.value.homeUrl
        || aboutContent.value.careerPage
        || aboutContent.value.socialMediaUrls.length
        || showCompanyRelations.value
    );
});

function jixAnalytics(label, action) {
    store.dispatch('jixAnalytics', { label, action });
}

function selectTab(tab) {
    store.dispatch('selectTab', { tab });
}

function getSOMEIcon(name) {
    return 'share-' + name;
}
</script>

<template>
    <div>
        <div v-if="aboutContent.aboutContentHtml" class="mb-3 bg-surface-default border" :class="{ 'p-3' : isFree }">
            <div v-html="aboutContent.aboutContentHtml" />
        </div>
        <CompanyProfileAvailableJobsSection class="d-md-none" @job-tab="emit('jobTab')" />
        <div class="row mt-4 text-sm">
            <div class="col-md-6" v-if="aboutContent.address">
                <div class="mb-3 bg-surface-default border">
                    <div class="p-3">
                        <div>
                            <strong>{{ T.headOffice() }}</strong>
                        </div>
                        <a @click="jixAnalytics('hovedkontor-kort-link','klik-menu-om-virksomheden')" :href="aboutContent.addressUrl">{{ aboutContent.address }}</a>
                    </div>
                    <div ref="mapRef" class="bg-surface-1" style="height: 280px" />
                </div>
            </div>
            <div class="col-md-6 d-flex flex-column">
                <div
                    v-if="showCompanyInfoBox"
                    class="mb-3 bg-surface-default border flex-grow-1 p-3"
                >
                    <div class="row flex-nowrap">
                        <div class="col overflow-hidden">
                            <div v-if="aboutContent.homeUrl" class="mb-3">
                                <strong>{{ T.homepage() }}</strong>
                                <div class="text-truncate">
                                    <a
                                        :title="aboutContent.homeUrl"
                                        @click="jixAnalytics('hjemmeside','klik-menu-om-virksomheden')"
                                        :href="aboutContent.homeUrl"
                                        rel="nofollow"
                                    >
                                        {{ aboutContent.homeUrl }}
                                    </a>
                                </div>
                            </div>
                            <div v-if="aboutContent.careerPage" class="mb-4">
                                <strong>{{ T.careerpage() }}</strong>
                                <div class="text-truncate">
                                    <a
                                        :title="aboutContent.careerPage"
                                        @click="jixAnalytics('karriereside','klik-menu-om-virksomheden')"
                                        :href="aboutContent.careerPage"
                                        rel="nofollow"
                                    >
                                        {{ aboutContent.careerPage }}
                                    </a>
                                </div>
                            </div>

                            <div class="social-share-toolbox">
                                <span v-for="[name, url] of aboutContent.socialMediaUrls" :key="name">
                                    <template v-if="url">
                                        <a
                                            :href="url"
                                            @click="jixAnalytics(name,'klik-menu-om-virksomheden')"
                                            target="_blank"
                                            :title="T.getTitle(name)"
                                            rel="nofollow"
                                        >
                                            <SvgIcon size="md" :name="getSOMEIcon(name)" />
                                        </a>
                                        &nbsp;
                                    </template>
                                </span>
                            </div>
                        </div>

                        <div class="col-6" v-if="showCompanyRelations">
                            <div class="mb-3">
                                <strong>{{ aboutContent.motherCompanyText }}</strong>
                                <div>
                                    <a
                                        @click="jixAnalytics('moderselskab','klik-menu-om-virksomheden')"
                                        :href="aboutContent.motherCompanyURL"
                                        rel="nofollow"
                                    >
                                        {{ aboutContent.motherCompany }}
                                    </a>
                                </div>
                            </div>
                            <div>
                                <strong>{{ aboutContent.daughterCompaniesText }}</strong>
                                <div v-for="(url, name) in aboutContent.daughterCompanies" :key="url">
                                    <a
                                        @click="jixAnalytics('datterselskab','klik-menu-om-virksomheden')"
                                        :href="url"
                                        rel="nofollow"
                                    >
                                        {{ name }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="isFree"
                    class="mb-3 bg-surface-default border p-3"
                >
                    <div>
                        <strong>{{ T.employeeHeader(companyName) }}</strong>
                    </div>
                    <div>
                        <p>
                            <a @click.prevent="selectTab('EVALUATIONS')" href="">
                                {{ T.employeeEvaluate() }}
                            </a>
                        </p>
                        <p>
                            {{ T.employeeShort() }}
                            <a :href="aboutContent.employeeShortUrl" @click="jixAnalytics('om-virksomhedsprofilen','klik-menu-om-virksomheden')">
                                {{ T.employeeShortLink() }}
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="isAdm">
            <div class="row">
                <div class="col-md-12">
                    <h3>{{ T.admin() }}</h3>
                </div>
            </div>
            <div class="row">
                <div class="col-md-8">
                    <div class="mb-3 bg-surface-default border">
                        <div class="p-3">
                            <h4> {{ clickHeader }} </h4>
                            <div class="row mt-4">
                                <div class="col-md-4">
                                    <div class="text-sm">
                                        {{ T.series1Legend() }}
                                    </div>
                                    <h2>{{ noExposures }}</h2>
                                </div>
                                <div class="col-md-8">
                                    <div class="text-sm">
                                        {{ T.series2Legend() }}
                                    </div>
                                    <h2>{{ noClicks }}</h2>
                                </div>
                            </div>
                            <Highcharts :options="highchartOptions" />
                        </div>
                        <hr class="m-0">
                        <select
                            class="form-control w-50 w-md-25 border-0 m-2"
                            v-model="timeInterval"
                        >
                            <option
                                v-for="[text, val] in statOptions"
                                :key="val"
                                :value="val"
                            >
                                {{ text }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="mb-3 bg-surface-default border p-3">
                        <div>
                            <strong>{{ T.moreActions() }}</strong>
                        </div>
                        <div v-for="action in actionOrder" :key="action">
                            <p />
                            <DsLinkBlock
                                v-if="aboutContent.moreActions[action]"
                                :href="aboutContent.moreActions[action]"
                            >
                                {{ T.actionText(action) }}
                            </DsLinkBlock>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="isAdmInternal" class="mb-3 bg-surface-default border p-3">
            <h3> Admin (intern) </h3>
            <ul class="mb-0">
                <li v-for="(admData, index) in aboutContent.internalAdm" :key="index">
                    <a :href="admData.url"> {{ T.internalAdmTexts(admData.name, isFree) }} </a>
                </li>
            </ul>
        </div>
    </div>
</template>
